import { Button, Skeleton } from 'antd';
import StatCard from 'components/atoms/StatCard';
import SubFilterDropdown from 'components/molecules/GrowthAnalysis/SubFilterDropdown';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getConfig, parseAttributeType } from 'utils/helperMethods';
import ButtonGroup from 'antd/lib/button/button-group';
import ProfitGrowthByHierarchyChart from './ProfitGrowthByHierarchyChart';
import MarginPercentByHierarchyChart from './MarginPercentByHierarchyChart';
import BreakDownTable from './BreakDownTable';
import ProfitPoolbyHierarchy from './ProfitPoolbyHierarchy';
// import StackedGroupedChart from '../StackedGroupedChart';
import { nullAttrTypeOption } from '../GrowthAnalysis';
import SubFilterCompareIcon from 'components/molecules/GrowthAnalysis/SubFilterCompareIcon';
import useStatCard from './hooks/useStatCard';
import { cloneDeep, concat, findLastIndex, isEmpty, startCase, uniqBy } from 'lodash';
import { useProfitPoolOverviewContext } from './context/ProfitPoolOverviewContext';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PP_OVERVIEW_TAB_CHANGED } from 'utils/mixpanelEventNames';

const ProfitPool = () => {
    const [tabActive, setTabActive] = useState('profit');

    const { appConfig } = useContext(UserContext);

    const commonConfig = getConfig('commonConfig', appConfig);
    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { defaultAttributeForData, excludedDimensions = [], kpiLabelMapping } = profitPoolConfig;

    const lastItemInHierarchy = useRef<any>({
        dim1: null,
        dim2: null,
        dim3: null,
    });

    const previousAppliedGlobalFilter = useRef<any>({
        filter: [],
        labels: [],
        previousSelectedItem: [],
    });

    const offsetRef = useRef({
        offset: [commonConfig.offset || 0, commonConfig.offset || 0],
        width: [0, 0],
    });

    const appliedGlobalFilter = useRef<string>('');
    const applyFilterObj = useRef<any>(null);

    //
    const {
        allDimAndAttributeTypeList,
        timePeriodLoading,
        idForRestOfComponent,
        profitPoolTierList,
        setIsTiersSingleSelect,
    } = useContext(FilterContextData);
    const { setSelectedSubFilter } = useProfitPoolOverviewContext();

    const [subFilterSelectedItems, setSubFilterSelectedItems] = useState([{ ...nullAttrTypeOption }]);
    const [activeCompareIcons, setActiveCompareIcons] = useState<any[]>([]);

    const [subFilterDropdownOptions, setSubFilterDropdownOptions] = useState({ data: [], loading: false });

    const [attributeTypeToShow, setAttributeTypesToShow] = useState<any[]>([]);

    const isProfitTabActive = tabActive === 'profit';

    const {
        data: statCardData,
        isPending: statCardDataLoading,
        isError: statCardDataError,
        isSuccess: statCardDataSuccess,
    } = useStatCard();

    const getKPILabelForTier = (tierList: any[], kpiName: string, includeTotalLabel: boolean) => {
        const labelMap: any = {};
        if (includeTotalLabel) {
            labelMap['Total'] = `Total ${kpiName}` as any;
        }
        tierList.map((_tier) => {
            labelMap[_tier?.tierReference] = `${_tier?.tierName} ${kpiName}`;
        });

        return labelMap;
    };

    const statCardLabelMapper = useMemo(() => {
        return getKPILabelForTier(profitPoolTierList, kpiLabelMapping?.['profit'] || 'Profit', true);
    }, [profitPoolTierList, kpiLabelMapping]);

    // Todo: To be Refactored

    const compareSubFilterClickHandler = (index: number, active: boolean) => {
        localStorage.setItem('compare_clicked', 'true');
        const tempActiveComparison = [...activeCompareIcons];
        if (!active) {
            // tempActiveComparison.push(index); //keep multiple active comparisons at a time
            tempActiveComparison[0] = index; //only keep 1 active comparison icon at a time
        } else {
            removeComparisonActiveItem(index, tempActiveComparison);
        }
        //resetting apply filter object
        resetApplyFilterObj();
        //for promo and incremental change sales tile to 1st item
        setActiveCompareIcons(tempActiveComparison);
    };

    const stringifiedGlobalFilter = useMemo(() => {
        const stringifiedGlobalFilter = JSON.stringify(idForRestOfComponent);
        //resetting apply filter object
        resetApplyFilterObj(stringifiedGlobalFilter, true);
        return stringifiedGlobalFilter;
    }, [idForRestOfComponent]);

    function resetApplyFilterObj(filter?: string, clearPreviousFilter: boolean = false) {
        applyFilterObj.current = null;
        appliedGlobalFilter.current = filter ? filter : stringifiedGlobalFilter;
        if (!filter || clearPreviousFilter)
            previousAppliedGlobalFilter.current = {
                filter: [],
                labels: [],
                previousSelectedItem: [],
            };
    }

    function removePreviousFilter() {
        if (activeCompareIcons.length) {
            resetApplyFilterObj();
        }
        appliedGlobalFilter.current = previousAppliedGlobalFilter.current.filter?.pop();
        previousAppliedGlobalFilter.current.labels?.pop();
        applyFilterObj.current = previousAppliedGlobalFilter.current.previousSelectedItem?.pop();
    }

    const removeComparisonActiveItem = (index: number, arr: any[]) => {
        const idx = arr.indexOf(index);
        if (idx > -1) arr.splice(idx, 1);
    };

    const checkIfNullAttributePresent = (arr: any[]) => {
        let nullExists = false;
        for (let i = 0; i < arr.length; i++) {
            if (!arr[i].value) nullExists = true;
        }
        return nullExists;
    };

    const onSubFilterOptionsChange = (val: any, item: any, index: number, filter?: any) => {
        const temp = [...subFilterSelectedItems];
        //if no item and val, it means event is triggered from clear icon
        if (!item && !val) {
            //if clearing last item and drilldown present
            if (index === subFilterSelectedItems.length - 2 && previousAppliedGlobalFilter.current.filter.length) {
                // subtracting 2 to remove null item and considering index here
                //if drilldown present then remove previous drilldown item
                removePreviousFilter();
            } //resetting apply filter object
            else resetApplyFilterObj(filter);
            const removedItem = temp[index];
            temp.splice(index, 1); //remove item if cleared
            const tempActiveComparison = [...activeCompareIcons];
            removeComparisonActiveItem(index, tempActiveComparison); //remove comparison icon also on clearing sub filter
            if (index > 0) removeComparisonActiveItem(index - 1, tempActiveComparison); //remove both left and right side comparisons
            if (tempActiveComparison.includes(index + 1))
                tempActiveComparison.splice(tempActiveComparison.indexOf(index + 1), 1, index); //update the compare value
            setActiveCompareIcons(tempActiveComparison);
            const isNullPresent = checkIfNullAttributePresent(temp);
            if (!isNullPresent && temp.length < subFilterDropdownOptions.data?.length) temp.push(nullAttrTypeOption); //add a new null sub filter if no null sub filter exists and its within the limit
        } else {
            //resetting apply filter object if there is no drilldown present or
            //if drilldown present and changing sub filters except the last subfilter dropdown, reset the apply filter object
            if (
                !previousAppliedGlobalFilter.current.filter.length ||
                (index < subFilterSelectedItems.length - 2 &&
                    !filter &&
                    previousAppliedGlobalFilter.current.filter.length)
            )
                resetApplyFilterObj(filter);

            temp.splice(index, 1, item); //replace item if changed
            if (temp.length < subFilterDropdownOptions.data?.length && temp.length - 1 === index)
                temp.push(nullAttrTypeOption); //add a new null sub filter if its within the limit
        }

        setSubFilterSelectedItems(temp);
    };

    const memoisedSubFilterDropdownOptions = useMemo(() => {
        const tempOptions: any[] = cloneDeep(subFilterDropdownOptions.data);
        subFilterSelectedItems.forEach((item) => {
            const attrTypeIndex = tempOptions.findIndex((elem: any) => elem.label === item?.label);
            if (attrTypeIndex > -1) tempOptions.splice(attrTypeIndex, 1);
        });
        if (tempOptions?.length > 0) {
            const dim3LastItemIndex = findLastIndex(tempOptions, (item) => item.dimension === 'dim3');
            lastItemInHierarchy.current.dim1 =
                tempOptions[findLastIndex(tempOptions, (item) => item.dimension === 'dim1')] || null;
            lastItemInHierarchy.current.dim2 =
                tempOptions[findLastIndex(tempOptions, (item) => item.dimension === 'dim2')] || null;
            lastItemInHierarchy.current.dim3 = dim3LastItemIndex === -1 ? null : tempOptions[dim3LastItemIndex];
        } //always store the last item in hierarchy for different dimensions
        return tempOptions;
    }, [subFilterSelectedItems, subFilterDropdownOptions]);

    function resetInfiniteScroller() {
        offsetRef.current.offset = [commonConfig.offset || 0, commonConfig.offset || 0];
        offsetRef.current.width = [0, 0];
    }

    const formatAttrTypeItems = (item: any) => {
        return {
            selectedAttributeType: item.label,
            selectedAttributeValue: item.value,
            isAttribute: item.isAttribute,
            dimension: item.dimension,
            dataset: [],
            disableHover:
                lastItemInHierarchy?.current[item.dimension] === null
                    ? true
                    : lastItemInHierarchy?.current?.[item.dimension]?.value <= item.value, //disable hover in bar chart for last item
            key: Math.random() + item.label, //adding a random key here so that the item gets re-rendered again
            dataKey: item?.key,
        };
    };

    const arrangeAttrType = (index: number) => {
        return [
            formatAttrTypeItems(subFilterSelectedItems[index]),
            formatAttrTypeItems(subFilterSelectedItems[index + 1]),
        ];
    };

    useEffect(() => {
        resetInfiniteScroller();
        //if comparison available, compare scenarios and show all scenarios user has compared
        if (activeCompareIcons.length > 0) {
            const sortedActiveComparisons = [...activeCompareIcons];
            sortedActiveComparisons.sort((a, b) => a - b); //sort active comparison indices in ascending order
            let tempAttrTypeToShow: any[] = [];

            for (let i = 0; i < sortedActiveComparisons.length; i++) {
                tempAttrTypeToShow.push(...arrangeAttrType(sortedActiveComparisons[i]));
            }
            tempAttrTypeToShow = uniqBy(tempAttrTypeToShow, (e) => e.selectedAttributeValue); //remove duplicate sub filter items
            setAttributeTypesToShow(tempAttrTypeToShow);
            setSelectedSubFilter(tempAttrTypeToShow[0]);

            //else if atleast 1 subFilter selected, only show latest sub filter selection
        } else if (subFilterSelectedItems?.length > 1) {
            for (let i = subFilterSelectedItems.length - 1; i >= 0; i--) {
                //finding the latest sub filter selection
                if (subFilterSelectedItems[i]?.value !== null) {
                    const tempAttrTypeToShow: any[] = [formatAttrTypeItems(subFilterSelectedItems[i])];
                    setSelectedSubFilter(tempAttrTypeToShow[0]);
                    setAttributeTypesToShow(tempAttrTypeToShow);
                    return;
                }
            }
            //else dont show anything
        } else {
            setAttributeTypesToShow([]);
            setSelectedSubFilter(defaultAttributeForData);
        }
    }, [activeCompareIcons, subFilterSelectedItems]);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            const data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions);
            if (data.length) {
                setSubFilterDropdownOptions({ data, loading: false });

                setSubFilterSelectedItems([
                    data.find((item: any) => item.label === defaultAttributeForData.attributeType) || data[0],
                    nullAttrTypeOption,
                ]);
            }
        }
    }, [allDimAndAttributeTypeList?.loading]);


    useEffect(() => {
        const PANEL_IDS = ['profit-pool-by-hierarchy-scroll', 'pint-chart-container'];

        const onScroll = (event: Event) => {
            const source = event.target as HTMLElement;
            const sourceIndex = PANEL_IDS.indexOf(source?.id ?? '');
            if (sourceIndex === -1) return;

            //this assignment fires an echo scroll event, which stops here because the offsets then match
            const target = document.getElementById(PANEL_IDS[1 - sourceIndex]);
            if (target && target.scrollLeft !== source.scrollLeft) target.scrollLeft = source.scrollLeft;
        };

        document.addEventListener('scroll', onScroll, true);
        return () => document.removeEventListener('scroll', onScroll, true);
    }, []);

    return (
        <>
            {/* {JSON.stringify(dummyData || {})} */}
            <div className="flex gap-2 mb-4">
                {subFilterSelectedItems.map((item, index) => {
                    return (
                        <div key={index} className="flex gap-2 items-center">
                            <SubFilterDropdown
                                loading={allDimAndAttributeTypeList?.loading || timePeriodLoading || false}
                                onChange={onSubFilterOptionsChange}
                                key={index}
                                index={index}
                                options={memoisedSubFilterDropdownOptions}
                                value={item.label}
                                from={'growth-analysis'}
                            />
                            {subFilterSelectedItems?.length - 1 !== index && (
                                <SubFilterCompareIcon
                                    disabled={subFilterSelectedItems[index + 1]?.label === null}
                                    onClick={compareSubFilterClickHandler}
                                    index={index}
                                    active={activeCompareIcons.includes(index)}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            <div
                className={`border border-borderLinePurpule bg-white p-4  rounded-lg            
            `}
            >
                <div className="mb-6 flex flex-row items-center gap-x-4">
                    <p className="font-semibold text-2xl">{'Profit Pool Overview'}</p>

                    <ButtonGroup className="rounded-lg bg-[#F7F5FF]">
                        <Button
                            onClick={() => {
                                setTabActive('profit');
                                setIsTiersSingleSelect(false);
                            }}
                            type={isProfitTabActive ? 'primary' : 'text'}
                        >
                            {'Profit'}
                        </Button>
                        <Button
                            type={isProfitTabActive ? 'text' : 'primary'}
                            onClick={() => {
                                trackEventMixpanel(PP_OVERVIEW_TAB_CHANGED, {
                                    selected_tab: 'growth',
                                    previous_tab: 'profit',
                                });
                                setTabActive('growth');
                                setIsTiersSingleSelect(true);
                            }}
                        >
                            {'Growth'}
                        </Button>
                    </ButtonGroup>
                </div>
                <div className="flex flex-row gap-4">
                    {statCardDataLoading ? (
                        <Skeleton active={true} paragraph={{ rows: 1, width: 256 }} title={{ width: 256 }} />
                    ) : (
                        statCardData.map((cardData: any) => {
                            return (
                                <StatCard
                                    metricName={statCardLabelMapper[cardData?.tier as string]}
                                    absoluteValue={cardData?.profit_val}
                                    percentChange={cardData?.percent_change}
                                    changedVal={cardData?.profit_val_diff_year_ago}
                                />
                            );
                        })
                    )}
                </div>
                <div className="mt-4 flex flex-col border border-[#0000000F] rounded-lg px-4 py-2 w-full">
                    {isProfitTabActive ? <ProfitPoolbyHierarchy /> : <ProfitGrowthByHierarchyChart />}
                </div>
                <div className="mt-4 flex flex-col border border-[#0000000F] rounded-lg px-4 py-2 w-full">
                    {isProfitTabActive ? <MarginPercentByHierarchyChart /> : <BreakDownTable />}
                </div>
            </div>
        </>
    );
};

export default ProfitPool;
